import { type ChangeEvent, type DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import {
  authFetch,
  getAccessToken,
  getDefaultAuthenticatedRoute,
  getRefreshToken,
  getStoredUser,
  REFERRAL_SESSION_KEY,
  storeAuthSession,
  type AuthUser,
} from '../utils/auth';

interface ReferralCaregiver {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  mobilePhone: string;
  phone: string;
  hhaStatus: string;
  hhaPortalStatus: string;
  hasPortalAccess: boolean;
  documentStatus: Record<string, boolean>;
}

interface ReferralDocumentField {
  label: string;
  name: string;
  field: string;
}

interface ReferralBackup {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

function ReferralLoadingOverlay() {
  return <LinearProgress />;
}

function storeReferralBackup() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const user = getStoredUser();
  if (!accessToken || !refreshToken || !user) return;
  const backup: ReferralBackup = { accessToken, refreshToken, user };
  localStorage.setItem(REFERRAL_SESSION_KEY, JSON.stringify(backup));
}

function fileInputId(caregiverId: string, field: string) {
  return `referral-upload-${caregiverId}-${field}`;
}

const referralTheme = {
  deep: '#330662',
  ultra: '#8900e1',
  medium: '#702b9d',
};

const referralGridSx = {
  backgroundColor: '#fafafa',
  border: '1px solid #e0e0e0',
  borderRadius: 2,
  '& .MuiDataGrid-cell': {
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    borderBottom: '1px solid #e0e0e0',
    px: 1,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#e0d1f2',
    },
    '&:focus, &:focus-within': {
      outline: '2px solid #8900e1',
      outlineOffset: '-2px',
      backgroundColor: '#e0d1f2',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiDataGrid-cell[data-field="actions"]': {
    cursor: 'default',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-topContainer': {
    backgroundColor: '#702b9d',
    color: 'white',
    fontWeight: 'bold',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'white',
    fontWeight: 'bold',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f3f0ff',
  },
  '& .MuiDataGrid-virtualScroller': {
    backgroundColor: 'white',
  },
  '& .MuiDataGrid-footerContainer': {
    minHeight: 52,
    backgroundColor: 'white',
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: 52,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    margin: 0,
  },
};

const allowedUploadExtensions = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg'];

function ReferralUploadZone({
  item,
  caregiver,
  uploaded,
  uploading,
  onUpload,
}: {
  item: ReferralDocumentField;
  caregiver: ReferralCaregiver;
  uploaded: boolean;
  uploading: boolean;
  onUpload: (caregiver: ReferralCaregiver, field: string, file?: File | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const uploadState = errorMessage ? 'error' : uploading ? 'uploading' : uploaded ? 'success' : 'idle';

  const handleFile = (file?: File | null) => {
    if (!file) return;
    const filename = file.name.toLowerCase();
    const extension = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '';
    if (!allowedUploadExtensions.includes(extension)) {
      setErrorMessage('Invalid file type');
      window.setTimeout(() => setErrorMessage(''), 5000);
      return;
    }
    setErrorMessage('');
    onUpload(caregiver, item.field, file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: { xs: '100%', sm: 215 },
        m: { xs: 0, sm: 1.5 },
        display: 'flex',
        flexDirection: 'column',
        borderColor: referralTheme.deep,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(51, 6, 98, 0.2)',
        },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 30, sm: 40 },
          height: { xs: 'auto', sm: 40 },
          p: { xs: 0.5, sm: 1 },
          backgroundColor: referralTheme.deep,
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          component="div"
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1,
            fontSize: { xs: '0.65rem', sm: '0.875rem' },
          }}
        >
          {item.label}
        </Typography>
      </CardContent>

      <CardContent
        role="button"
        tabIndex={0}
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 0.75, sm: 1.5 },
          cursor: 'pointer',
          border: `2px dashed ${referralTheme.medium}`,
          borderTop: 'none',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          minHeight: { xs: 90, sm: 110 },
          height: { xs: 90, sm: 110 },
          transition: 'all 0.3s ease',
          backgroundColor: dragOver ? 'rgba(137, 0, 225, 0.1)' : undefined,
          borderColor: dragOver ? referralTheme.ultra : referralTheme.medium,
          '&:hover': {
            backgroundColor: 'rgba(137, 0, 225, 0.05)',
            borderColor: referralTheme.ultra,
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          id={fileInputId(caregiver.id, item.field)}
          type="file"
          accept={allowedUploadExtensions.join(',')}
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          {uploadState === 'idle' && (
            <>
              <CloudUploadIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: referralTheme.medium, mb: 0.25 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, width: '100%', overflowWrap: 'break-word' }}
              >
                Drag file or browse
              </Typography>
            </>
          )}

          {uploadState === 'uploading' && (
            <>
              <CircularProgress size={24} sx={{ mb: 0.25, color: referralTheme.ultra }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, width: '100%' }}>
                Uploading
              </Typography>
            </>
          )}

          {uploadState === 'success' && (
            <>
              <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: referralTheme.ultra, mb: 0.25 }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, color: referralTheme.ultra, width: '100%' }}>
                Upload Successful!
              </Typography>
            </>
          )}

          {uploadState === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: 'error.main', mb: 0.25 }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, color: 'error.main', width: '100%' }}>
                {errorMessage}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Referral() {
  const currentUser = getStoredUser();
  const [caregivers, setCaregivers] = useState<ReferralCaregiver[]>([]);
  const [documentFields, setDocumentFields] = useState<ReferralDocumentField[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [uploadCaregiver, setUploadCaregiver] = useState<ReferralCaregiver | null>(null);

  const isActionLoading = (key: string) => Boolean(actionLoading[key]);
  const setActionLoadingKey = (key: string, isLoading: boolean) => {
    setActionLoading((previous) => {
      const next = { ...previous };
      if (isLoading) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const loadCaregivers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/referral/caregivers');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to load caregivers');
      setCaregivers(data.caregivers || []);
      setDocumentFields(data.documentFields || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load caregivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCaregivers();
  }, []);

  const filteredCaregivers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return caregivers;
    const digitsQuery = query.replace(/\D/g, '');
    return caregivers.filter((caregiver) => (
      caregiver.fullName.toLowerCase().includes(query)
      || caregiver.email.toLowerCase().includes(query)
      || (digitsQuery.length > 0 && caregiver.mobilePhone.replace(/\D/g, '').includes(digitsQuery))
    ));
  }, [caregivers, search]);

  const loginAsCaregiver = async (caregiver: ReferralCaregiver) => {
    const actionKey = `${caregiver.id}:login`;
    if (isActionLoading(actionKey)) return;
    setActionLoadingKey(actionKey, true);
    setError(null);
    try {
      const response = await authFetch(`/api/referral/caregivers/${encodeURIComponent(caregiver.id)}/impersonate`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to log in as caregiver');
      storeReferralBackup();
      storeAuthSession(data.accessToken, data.refreshToken, data.user);
      window.location.assign(getDefaultAuthenticatedRoute(data.user));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log in as caregiver');
    } finally {
      setActionLoadingKey(actionKey, false);
    }
  };

  const uploadDocument = async (caregiver: ReferralCaregiver, field: string, file?: File | null) => {
    if (!file) return;
    const actionKey = `${caregiver.id}:${field}`;
    if (isActionLoading(actionKey)) return;
    setActionLoadingKey(actionKey, true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('field', field);
      formData.append('file', file);
      const response = await authFetch(`/api/referral/caregivers/${encodeURIComponent(caregiver.id)}/documents`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to upload document');
      setCaregivers((previous) => previous.map((item) => (
        item.id === caregiver.id ? { ...item, documentStatus: data.documentStatus || item.documentStatus } : item
      )));
      setUploadCaregiver((previous) => (
        previous?.id === caregiver.id
          ? { ...previous, documentStatus: data.documentStatus || previous.documentStatus }
          : previous
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setActionLoadingKey(actionKey, false);
    }
  };

  const columns: GridColDef<ReferralCaregiver>[] = [
    { field: 'fullName', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'mobilePhone', headerName: 'Mobile', flex: 1, minWidth: 120 },
    { field: 'hhaStatus', headerName: 'HHA Status', flex: 1, minWidth: 140 },
    { field: 'hhaPortalStatus', headerName: 'HHA Portal Status', flex: 1, minWidth: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      width: 200,
      renderCell: (params) => {
        const caregiver = params.row;
        return (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
            <Button
              variant="contained"
              size="small"
              disabled={!caregiver.hasPortalAccess || isActionLoading(`${caregiver.id}:login`)}
              onClick={caregiver.hasPortalAccess ? () => void loginAsCaregiver(caregiver) : undefined}
              sx={{
                bgcolor: caregiver.hasPortalAccess ? '#330662 !important' : '#cccccc !important',
                color: caregiver.hasPortalAccess ? '#fff !important' : '#666666 !important',
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.7rem',
                '&:hover': {
                  bgcolor: caregiver.hasPortalAccess ? '#702b9d !important' : '#cccccc !important',
                },
              }}
            >
              {isActionLoading(`${caregiver.id}:login`) ? 'Loading...' : 'Login'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon sx={{ fontSize: '14px !important' }} />}
              disabled={isActionLoading(`${caregiver.id}:upload`)}
              onClick={() => setUploadCaregiver(caregiver)}
              sx={{
                borderColor: '#330662',
                color: '#330662',
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.7rem',
                '&:hover': {
                  borderColor: '#702b9d',
                  color: '#702b9d',
                  backgroundColor: 'rgba(51, 6, 98, 0.04)',
                },
              }}
            >
              Upload
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: 400,
            borderRadius: 1,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Referral Portal
          </Typography>

          <Typography variant="h6" color="text.secondary" gutterBottom>
            Welcome, {currentUser?.name || 'Recruitment Agency'}!
          </Typography>

          <Box sx={{ mt: 4, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Access Caregiver Account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click &quot;Login&quot; to access one of your referred caregivers&apos; accounts to help
              them complete their application. Use &quot;Upload&quot; to upload documents directly for the caregiver.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or mobile..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              inputProps={{ 'aria-label': 'Search caregivers' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#702b9d' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#702b9d',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8900e1',
                  },
                },
              }}
            />

            <Box sx={{ height: 500, width: '100%', mb: 3 }}>
              <DataGridPremium
                rows={filteredCaregivers}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnSelector
                slots={{ loadingOverlay: ReferralLoadingOverlay }}
                localeText={{
                  noRowsLabel: search ? `No caregivers match "${search}"` : 'No caregivers found for your agency',
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                sx={referralGridSx}
              />
            </Box>

            {caregivers.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {search
                  ? `${filteredCaregivers.length} of ${caregivers.length} caregiver${caregivers.length !== 1 ? 's' : ''} shown`
                  : `${caregivers.length} caregiver${caregivers.length !== 1 ? 's' : ''} found`}
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>

      <Dialog
        open={Boolean(uploadCaregiver)}
        onClose={() => setUploadCaregiver(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: 1200,
            maxHeight: '90vh',
            borderRadius: 2,
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
            <Typography variant="h6" component="h2" sx={{ color: referralTheme.deep, fontWeight: 'bold' }}>
              Upload Documents for {uploadCaregiver?.fullName || 'Caregiver'}
            </Typography>
            <Button
              onClick={() => setUploadCaregiver(null)}
              aria-label="Close upload dialog"
              sx={{
                minWidth: 'auto',
                color: referralTheme.deep,
                '&:hover': {
                  backgroundColor: 'rgba(51, 6, 98, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          </Box>

          <Box
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Button
                sx={{
                  fontWeight: 'bold',
                  color: referralTheme.deep,
                  textTransform: 'none',
                  borderRadius: 0,
                  px: 3,
                  py: 1.5,
                  borderBottom: `2px solid ${referralTheme.deep}`,
                  '&:hover': {
                    color: referralTheme.ultra,
                    backgroundColor: 'rgba(51, 6, 98, 0.04)',
                  },
                }}
              >
                Documents
              </Button>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  justifyItems: 'center',
                  gap: { xs: 1, sm: 2 },
                }}
              >
                {uploadCaregiver && documentFields.map((item) => (
                  <ReferralUploadZone
                    key={item.field}
                    item={item}
                    caregiver={uploadCaregiver}
                    uploaded={Boolean(uploadCaregiver.documentStatus?.[item.field])}
                    uploading={isActionLoading(`${uploadCaregiver.id}:${item.field}`)}
                    onUpload={(caregiver, field, file) => void uploadDocument(caregiver, field, file)}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
